<script setup lang="ts">
import {
  useI18n,
  useComputedSettings,
  Tooltip,
  useDeviceFeatures,
} from '@thehotelsnetwork/widgets-core';
import { NotificationInfoIcon } from '@thehotelsnetwork/icons/vue/linear';
import type { ReviewAverages } from '@thehotelsnetwork/personalization-types';

type Colors = {
  darkText: string;
  lightText: string;
}
const props = withDefaults(
  defineProps<{
    data: ReviewAverages;
    colors: Colors;
    orientation?: 'horizontal' | 'vertical';
    compactSize?: 'big' | 'small' | false;
    fontSize?: 'small' | 'large';
    showInfoBtn?: boolean;
    tooltipPlacement?: 'top' | 'bottom';
    tooltipZIndex?: number;
  }>(),
  {
    ratingBase: 5,
    orientation: 'horizontal',
    compactSize: false,
    fontSize: 'small',
    tooltipPlacement: 'top',
    tooltipZIndex: 99999,
  },
);
const { t } = useI18n();
const computedSettings = useComputedSettings();
const { isHoverCapable } = useDeviceFeatures();
</script>
<template>
  <div
    :class="[
      'main-rating',
      `main-rating--${props.compactSize ? 'horizontal' : props.orientation}`,
      `main-rating--${props.fontSize}-fontsize`,
      props.compactSize ? `main-rating--compact-${props.compactSize}` : undefined,
    ]"
    data-testid="main-rating"
  >
    <p class="main-rating__score lightext">
      <span class="main-rating__average darktext">
        {{ props.data.averageFormatted }}
      </span>
      <span>
        /
      </span>
      <span>
        {{ props.data.maxScore }}
      </span>
    </p>
    <div :class="{ 'flex flex-col items-center': props.orientation === 'vertical' }">
      <div class="flex items-center space-x-1 darktext leading-4">
        <p class="main-rating__note">
          {{ props.data.noteFormatted }}
        </p>
        <Tooltip
          v-if="props.showInfoBtn"
          :mode="isHoverCapable ? 'hover' : 'click'"
          :placement="props.tooltipPlacement"
          :z-index-tooltip="computedSettings.tooltipZIndex"
          :container="computedSettings.tooltipContainer"
        >
          <template #trigger>
            <button
              type="button"
              class="main-rating__info-button"
            >
              <NotificationInfoIcon
                class="w-4 h-4"
              />
            </button>
          </template>
          <template #tooltip>
            <div class="py-2 px-3">
              <slot name="providersInfo" />
            </div>
          </template>
        </Tooltip>
      </div>
      <div class="text-xs lightext">
        <p class="main-rating__based main-rating__based--large">
          {{ t('based on {number} reviews', { number: props.data.totalReviews }) }}
        </p>
        <p class="main-rating__based main-rating__based--short">
          {{ t('{num} reviews', { num: props.data.totalReviews }) }}
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.darktext {
  color: v-bind('$props.colors.darkText');
}
.lightext {
  color: v-bind('$props.colors.lightText');
}
.main-rating {
  display: flex;
  align-items: center;
}
.main-rating--vertical {
  text-align: center;
  flex-direction: column;
}
.main-rating--vertical .main-rating__based--large {
  display: none;
}
.main-rating--horizontal {
  flex-direction: row;
  text-align: left;
  gap: 0.5rem;
}
.main-rating--horizontal .main-rating__based--large {
  display: block;
}
.main-rating--horizontal .main-rating__based--short {
  display: none;
}
.main-rating__score {
  @apply text-base font-semibold items-center lightext flex gap-0.5;
}
.main-rating__average {
  @apply text-3xl;
  @apply leading-8;
}
[class*='main-rating--compact'] .main-rating__average {
  @apply text-2xl;
}
.main-rating--compact-small .main-rating__score {
  @apply text-sm;
}
[class*='main-rating--compact'] .main-rating__based--large {
  display: none;
}
[class*='main-rating--compact'] .main-rating__based--short {
  display: block;
  line-height: 1.2;
}
.main-rating--large-fontsize .main-rating__score {
  @apply text-2xl mb-2;
}
.main-rating--large-fontsize .main-rating__average {
  @apply text-4xl;
}
.main-rating__note {
  @apply text-sm font-semibold;
}
.main-rating--large-fontsize .main-rating__note {
  @apply text-lg;
}
.main-rating--large-fontsize .main-rating__based {
  @apply text-base;
}
</style>
